// SPDX-License-Identifier: AGPL-3.0-only

import { defineStore } from "pinia";
import {
  addTenantMember,
  changeTenantMemberStatus,
  listTenantMembers,
  removeTenantMember,
  updateTenantMemberRole,
  type AddTenantMemberInput,
  type TenantMember,
  type TenantMembershipStatus,
} from "../api/tenant-members.js";
import type { TenantRole } from "../api/tenants.js";

export type TenantMemberStoreStatus = "idle" | "loading" | "ready" | "failed";

interface TenantMemberState {
  status: TenantMemberStoreStatus;
  tenantId: string | null;
  members: TenantMember[];
}

function sortMembers(members: readonly TenantMember[]): TenantMember[] {
  return [...members].sort((left: TenantMember, right: TenantMember): number => {
    const leftRank: number = left.membershipStatus === "disabled" ? 2 : left.userStatus === "active" ? 0 : 1;
    const rightRank: number = right.membershipStatus === "disabled" ? 2 : right.userStatus === "active" ? 0 : 1;
    const statusOrder: number = leftRank - rightRank;
    if (statusOrder !== 0) return statusOrder;
    const nameOrder: number = left.displayName.localeCompare(right.displayName);
    return nameOrder === 0 ? left.email.localeCompare(right.email) : nameOrder;
  });
}

export const useTenantMemberStore = defineStore("tenant-members", {
  state: (): TenantMemberState => ({ status: "idle", tenantId: null, members: [] }),
  actions: {
    async load(tenantId: string): Promise<void> {
      if (this.tenantId === tenantId && (this.status === "loading" || this.status === "ready")) {
        return;
      }
      this.status = "loading";
      this.tenantId = tenantId;
      this.members = [];
      try {
        this.members = sortMembers(await listTenantMembers(tenantId));
        this.status = "ready";
      } catch (error: unknown) {
        this.status = "failed";
        throw error;
      }
    },
    async add(input: AddTenantMemberInput): Promise<TenantMember> {
      if (this.tenantId === null) {
        throw new Error("No tenant is selected for member management");
      }
      const member: TenantMember = await addTenantMember(this.tenantId, input);
      this.members = sortMembers([...this.members, member]);
      return member;
    },
    async changeRole(userId: string, role: TenantRole): Promise<TenantMember> {
      if (this.tenantId === null) {
        throw new Error("No tenant is selected for member management");
      }
      const member: TenantMember = await updateTenantMemberRole(this.tenantId, userId, role);
      this.members = sortMembers(
        this.members.map((item: TenantMember): TenantMember =>
          item.userId === userId ? member : item,
        ),
      );
      return member;
    },
    async changeStatus(
      userId: string,
      status: TenantMembershipStatus,
    ): Promise<TenantMember> {
      if (this.tenantId === null) {
        throw new Error("No tenant is selected for member management");
      }
      const member: TenantMember = await changeTenantMemberStatus(
        this.tenantId,
        userId,
        status,
      );
      this.members = sortMembers(this.members.map(
        (item: TenantMember): TenantMember => item.userId === userId ? member : item,
      ));
      return member;
    },
    async remove(userId: string): Promise<void> {
      if (this.tenantId === null) {
        throw new Error("No tenant is selected for member management");
      }
      await removeTenantMember(this.tenantId, userId);
      this.members = this.members.filter((member: TenantMember): boolean => member.userId !== userId);
    },
    reset(): void {
      this.status = "idle";
      this.tenantId = null;
      this.members = [];
    },
  },
});
